import React, { useEffect, useState, useCallback } from "react";
import api from "../../api";
import { useTranslation } from "react-i18next";
import { Search, FileText, ChevronUp, ChevronDown, Banknote } from "lucide-react";
import MissionEditModal from "../Mission/MissionEditModal";
import { toast } from "react-toastify";
import { utils, writeFile } from "xlsx";

const SalaryManagementPage = () => {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [missions, setMissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 10;

  const [selectedMission, setSelectedMission] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState("missionDate");
  const [sortOrder, setSortOrder] = useState("asc");

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get("/mission-employees");
      setEmployees(res.data);
    } catch (err) {
      toast.error(t("salary.fetchError"));
    }
  }, [t]);

  const fetchMissions = useCallback(async () => {
    try {
      const res = await api.get("/missions");
      setMissions(res.data?.data || []);
    } catch (err) {
      toast.error(t("salary.fetchError"));
    }
  }, [t]);

  useEffect(() => {
    fetchEmployees();
    fetchMissions();
  }, [fetchEmployees, fetchMissions]);

  const handleMarkPaid = async (id, isPaid) => {
    setEmployees(prev =>
      prev.map(emp =>
        emp.id === id ? { ...emp, is_paid: isPaid } : emp
      )
    );
    try {
      await api.patch(`/mission-employees/${id}/pay`, { is_paid: isPaid });
      toast.success(isPaid ? t("salary.statusUpdatedPaid") : t("salary.statusUpdatedUnpaid"));
      fetchEmployees();
    } catch (err) {
      toast.error(t("salary.updatePaymentError"));
      setEmployees(prev =>
        prev.map(emp =>
          emp.id === id ? { ...emp, is_paid: !isPaid } : emp
        )
      );
    }
  };

  const handleEditMission = (missionId) => {
    const missionToEdit = missions.find((m) => m.id === missionId);
    if (missionToEdit) {
      setSelectedMission(missionToEdit);
      setEditModalOpen(true);
    } else {
      toast.error(t("salary.missionNotFound"));
    }
  };

  const handleEditSave = async (updatedMission) => {
    try {
      await api.put(`/missions/${updatedMission.id}`, updatedMission);
      toast.success(t("salary.missionUpdated"));
      setEditModalOpen(false);
      fetchMissions();
      fetchEmployees();
    } catch (err) {
      toast.error(t("salary.updateMissionError"));
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleMonthFilterChange = (e) => {
    setMonthFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const employeesWithMissionInfo = employees.map((emp) => {
    const mission = missions.find((m) => m.id === emp.mission_id);
    return {
      ...emp,
      missionDate: mission ? mission.date : "-",
      missionName: mission ? mission.name || mission.type || "-" : "-",
      clientName: mission ? mission.client || "-" : "-",
    };
  });

  const sortedFilteredEmployees = employeesWithMissionInfo
    .filter((emp) => {
      const matchesSearch =
        emp.employee_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (emp.missionName && emp.missionName.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (emp.clientName && emp.clientName.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "paid" && emp.is_paid) ||
        (statusFilter === "unpaid" && !emp.is_paid);
      const matchesMonth =
        !monthFilter ||
        (emp.missionDate && emp.missionDate.startsWith(monthFilter));
      return matchesSearch && matchesStatus && matchesMonth;
    })
    .sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue = a[sortColumn];
      let bValue = b[sortColumn];
      if (sortColumn === "missionDate") {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(sortedFilteredEmployees.length / perPage);
  const paginatedEmployees = sortedFilteredEmployees.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const handleExportExcel = () => {
    const timestamp = new Date().toISOString().replace(/[:.-]/g, "");
    const filename = `Salaries_${timestamp}.xlsx`;
    const data = sortedFilteredEmployees.map((emp) => ({
      "Mission Name": emp.missionName || "-",
      "Client": emp.clientName || "-",
      "Mission Date": emp.missionDate || "-",
      "Employee Name": emp.employee_name,
      "Salary (€)": emp.salary,
      "Paid Status": emp.is_paid ? t("salary.paid") : t("salary.unpaid"),
    }));
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, "Salaries");
    writeFile(wb, filename);
    toast.success(t("salary.exportSuccess"));
  };

  const uniqueMonths = Array.from(
    new Set(
      missions
        .map((m) => m.date?.slice(0, 7))
        .filter(Boolean)
    )
  ).sort().reverse();

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return null;
    return sortOrder === "asc"
      ? <ChevronUp className="w-3 h-3 inline ml-1" />
      : <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Banknote className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-base-content">
            {t("salary.salaryManagement")}
          </h1>
          <p className="text-sm text-base-content/40 mt-0.5">
            {t("salary.salaryManagementSubtitle") || "Gestion des salaires et paiements"}
          </p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-3">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-base-content/40" />
          <input
            type="text"
            placeholder={t("salary.searchPlaceholder")}
            value={searchQuery}
            onChange={handleSearchChange}
            className="input input-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none pl-9 w-full"
          />
        </div>

        <select
          value={statusFilter}
          onChange={handleStatusFilterChange}
          className="select select-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
        >
          <option value="all">{t("salary.all")}</option>
          <option value="paid">{t("salary.paid")}</option>
          <option value="unpaid">{t("salary.unpaid")}</option>
        </select>

        <select
          value={monthFilter}
          onChange={handleMonthFilterChange}
          className="select select-bordered bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
        >
          <option value="">{t("salary.allMonths")}</option>
          {uniqueMonths.map((month) => (
            <option key={month} value={month}>{month}</option>
          ))}
        </select>

        <button
          onClick={handleExportExcel}
          className="btn btn-primary gap-2 shrink-0"
        >
          <FileText className="w-4 h-4" />
          {t("salary.exportExcel")}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm">
        <table className="table table-sm">
          <thead className="bg-base-200 border-b border-base-200">
            <tr>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">
                {t("salary.missionName")}
              </th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3">
                {t("salary.clientName")}
              </th>
              <th
                className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 cursor-pointer select-none"
                onClick={() => handleSort("missionDate")}
              >
                {t("salary.missionDate")}
                <SortIcon column="missionDate" />
              </th>
              <th
                className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 cursor-pointer select-none"
                onClick={() => handleSort("employee_name")}
              >
                {t("salary.employeeName")}
                <SortIcon column="employee_name" />
              </th>
              <th
                className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 cursor-pointer select-none"
                onClick={() => handleSort("salary")}
              >
                {t("salary.salaryAmount")}
                <SortIcon column="salary" />
              </th>
              <th
                className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 text-center cursor-pointer select-none"
                onClick={() => handleSort("is_paid")}
              >
                {t("salary.isPaid")}
                <SortIcon column="is_paid" />
              </th>
              <th className="text-base-content/50 text-[11px] font-semibold tracking-wider uppercase py-3 text-center">
                {t("salary.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-base-200">
            {paginatedEmployees.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-16 text-center text-base-content/40 text-sm">
                  {t("salary.noResults") || "Aucun résultat"}
                </td>
              </tr>
            ) : (
              paginatedEmployees.map((emp) => (
                <tr key={emp.id} className="hover:bg-base-200/50 transition-colors group">
                  <td className="py-3 text-base-content text-sm">{emp.missionName || "-"}</td>
                  <td className="py-3 text-base-content/70 text-sm">{emp.clientName || "-"}</td>
                  <td className="py-3 text-base-content/70 text-sm">{emp.missionDate || "-"}</td>
                  <td className="py-3 text-base-content font-medium text-sm">{emp.employee_name}</td>
                  <td className="py-3">
                    <span className="font-semibold text-primary text-sm">{emp.salary} €</span>
                  </td>
                  <td className="py-3 text-center">
                    <select
                      value={emp.is_paid ? "paid" : "unpaid"}
                      onChange={(e) => handleMarkPaid(emp.id, e.target.value === "paid")}
                      className={`select select-xs font-semibold border rounded-lg text-xs ${
                        emp.is_paid
                          ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
                          : "bg-error/10 text-error border-error/20"
                      }`}
                      style={{ minWidth: 90 }}
                    >
                      <option value="paid">{t("salary.paid")}</option>
                      <option value="unpaid">{t("salary.unpaid")}</option>
                    </select>
                  </td>
                  <td className="py-3 text-center">
                    <button
                      onClick={() => handleEditMission(emp.mission_id)}
                      className="btn btn-xs btn-ghost border border-base-300 text-base-content/60 hover:text-base-content hover:bg-base-300 opacity-0 group-hover:opacity-100 transition-all"
                    >
                      {t("salary.editMission")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-1 mt-6">
          {Array.from({ length: totalPages }, (_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`btn btn-sm ${
                currentPage === idx + 1
                  ? "btn-primary"
                  : "btn-ghost border border-base-200 text-base-content/60"
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}

      {editModalOpen && selectedMission && (
        <MissionEditModal
          mission={selectedMission}
          onClose={() => setEditModalOpen(false)}
          onSave={handleEditSave}
        />
      )}
    </div>
  );
};

export default SalaryManagementPage;
